/**
 * Bumped-items (bumps) helpers shared by every product list and the product page.
 *
 * The API sends `bumped` on items only for buyers in the test, so everything here is keyed off
 * the field being present: outside the test no label renders and the analytics events stay
 * byte-identical. Ownership is resolved here because list bodies are route-cached across users;
 * a seller never sees their own item as bumped.
 */

export interface BumpableProduct {
  bumped?: boolean;
  isOwner?: boolean | null;
  shopId?: number | null;
}

/**
 * Whether the viewer should see this item as bumped. `isOwner` comes from a route-cached body
 * that may have been populated by another user, so the viewer's own shop id decides as well.
 */
export function isBumped(product: object | null | undefined, viewerShopId?: number | null): boolean {
  const { bumped, isOwner, shopId } = (product ?? {}) as BumpableProduct;
  const owned = !!isOwner || (!!shopId && !!viewerShopId && shopId === viewerShopId);

  return !!bumped && !owned;
}

/** The per-item analytics marker: present only when the API marked the item at all. */
export function bumpedMarker(product: object | null | undefined, viewerShopId?: number | null): { item_bumped: boolean } | undefined {
  if ((product as BumpableProduct | null | undefined)?.bumped === undefined) {
    return undefined;
  }

  return { item_bumped: isBumped(product, viewerShopId) };
}

/** Event-level count alongside the item markers; undefined when no item on the page carries one. */
export function bumpedCount(markers: ({ item_bumped?: boolean } | undefined)[]): { bumped_count: number } | undefined {
  if (!markers.some((marker) => marker?.item_bumped !== undefined)) {
    return undefined;
  }

  return { bumped_count: markers.filter((marker) => marker?.item_bumped === true).length };
}
